export class VideoControls {
  constructor() {
    this.selectors = {
      container: "[js-video-controls]",
      video: "video",
    };
    this.observedContainers = new WeakSet();
    this.handleEvent = this.handleEvent.bind(this);

    this.init();
  }

  init(root = document) {
    this.initVideoClasses(root);
    this.handleIntersectionAutoplay(root);

    if (!this.eventBound) {
      document.body.addEventListener("click", this.handleEvent);
      this.eventBound = true;
    }
  }

  handleEvent(e) {
    this.handlePausePlay(e);
  }

  prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  }

  handleIntersectionAutoplay(root = document) {
    let options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.5,
    };

    const videoContainers = root.querySelectorAll(this.selectors.container);
    videoContainers.forEach((container) => {
      if (this.observedContainers.has(container)) return;

      const video = container.querySelector(this.selectors.video);
      if (!video) return;

      this.observedContainers.add(container);

      let callback = (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            video.pause();
            container.classList.remove("video-playing");
          } else if (entry.isIntersecting) {
            // A video with no controls of any kind that reduced motion stops
            // from autoplaying can never be played, so never fetch it either.
            const hasNoControls =
              container.dataset.customControls === "false" && !video.hasAttribute("controls");
            if (this.prefersReducedMotion() && hasNoControls) return;

            // Load video source if it hasn't been loaded yet
            this.loadVideoSource(video).then(() => {
              // Only auto-play if autoplay is enabled and not manually paused
              const shouldAutoplay =
                !this.prefersReducedMotion() &&
                (video.getAttribute("autoplay") === "true" ||
                  video.getAttribute("autoplay") === "autoplay");

              if (shouldAutoplay && !container.classList.contains("video-manual-pause")) {
                video.play().catch((e) => {
                  console.warn("VideoControls: autoplay was prevented", e);
                });
                container.classList.remove("video-paused");
                container.classList.add("video-playing");
              }
            });
          }
        });
      };

      // Always observe videos for lazy loading, but only autoplay if autoplay is enabled
      let observer = new IntersectionObserver(callback, options);
      if (container && video) observer.observe(container);
    });
  }

  loadVideoSource(video) {
    return new Promise((resolve) => {
      // Check if video sources need to be loaded from data-src
      const sources = video.querySelectorAll("source[data-src]");
      let sourcesLoaded = false;

      sources.forEach((source) => {
        if (source.hasAttribute("data-src")) {
          source.src = source.getAttribute("data-src");
          source.removeAttribute("data-src");
          sourcesLoaded = true;
        }
      });

      // Reload the video element to use the new sources
      if (sourcesLoaded) {
        video.load();

        // Wait for video to be ready to play
        const handleCanPlay = () => {
          video.removeEventListener("canplay", handleCanPlay);
          resolve();
        };

        video.addEventListener("canplay", handleCanPlay);

        // Fallback timeout in case canplay doesn't fire
        setTimeout(() => {
          video.removeEventListener("canplay", handleCanPlay);
          resolve();
        }, 3000);
      } else {
        // No sources to load, resolve immediately
        resolve();
      }
    });
  }

  initVideoClasses(root = document) {
    const videoContainers = root.querySelectorAll(this.selectors.container);

    videoContainers.forEach((container) => {
      const video = container.querySelector(this.selectors.video);

      if (video) {
        if (video.paused) {
          container.classList.add("video-paused");
          container.classList.remove("video-playing");
        } else {
          container.classList.add("video-playing");
          container.classList.remove("video-paused");
        }
      }
    });
  }

  handlePausePlay(e) {
    const target = e.target;

    if (target.closest(this.selectors.container)) {
      const container = target.closest(this.selectors.container);

      if (
        container.dataset.customControls !== "true" &&
        container.dataset.customControls !== "auto"
      ) {
        return;
      }

      const video = container.querySelector(this.selectors.video);
      const control = target.closest(".video-control-icon");

      if (video) {
        if (video.paused) {
          this.loadVideoSource(video).then(() => {
            video.play().catch((e) => {
              console.warn("VideoControls: play was prevented", e);
            });
            container.classList.add("video-playing");
            container.classList.remove("video-paused", "video-manual-pause");
            if (control) container.querySelector(".pause-icon")?.focus();
          });
        } else {
          video.pause();
          container.classList.add("video-paused", "video-manual-pause");
          container.classList.remove("video-playing");
          if (control) container.querySelector(".play-icon")?.focus();
        }
      }
    }
  }
}

export const videoControls = new VideoControls();
window.VideoControls = videoControls;
